'use client';

import React from 'react';
import { Slide, SliderButton, SliderProvider, SliderTrack } from '@faceless-ui/slider';
import { useWindowInfo } from '@faceless-ui/window-info';

import type { PagesQuery } from '../../graphql/generated/schema';
import { BlockSettings_Margin_MarginBottom, BlockSettings_Padding_PaddingLeft, BlockSettings_Padding_PaddingRight } from '../../graphql/generated/schema';
import type { ExtractLayoutType, SharedBlockType } from '../types';
import { Button, RenderBlocks } from '../../components';
import styles from './styles.module.scss';
import { constructClassName } from '../../utils/constructClassName';
import queries from '../../styles/queries/index.module.scss';
import colors from '../../styles/colors/index.module.scss';
import { BlockContainer } from '../../components/layouts';

export const SliderWithBlocks: React.FC<SharedBlockType & ExtractLayoutType<PagesQuery, 'SliderWithBlocks'>> = ({
  sliderWithBlocks,
  slidesToShow: slidesToShowProp,
  type,
  paginationType,
  autoScrollEnabled,
  blockSettings,
  autoScrollSpeed,
  blockType
}) => {
  const windowInfo = useWindowInfo();

  const slidesToShow = (() => {
    switch (true) {
      case (windowInfo.width || 0) >= parseInt(queries.lg, 10) && (paginationType !== 'blockName'):
        return slidesToShowProp?.desktop || 3;
      case (windowInfo.width || 0) >= parseInt(queries.sm, 10) && (paginationType !== 'blockName'):
        return slidesToShowProp?.tablet || 2;
      case (windowInfo.width || 0) >= parseInt(queries.xxs, 10):
        return slidesToShowProp?.mobile || 1;
      default:
        return 1;
    }
  })();

  return (
    <BlockContainer
      className={styles.container}
      blockSettings={blockSettings}
    >
      <SliderProvider
        slidesToShow={slidesToShow}
        dragScroll={type !== 'marquee'}
        marquee={type === 'marquee'}
        pauseOnHover={false}
        scrollOffset={slidesToShow === 1 ? 0 : 40}
        autoPlay={type !== 'marquee' && Boolean(autoScrollEnabled)}
        autoplaySpeed={autoScrollSpeed || 5000}
      >
        {
          ({ currentSlideIndex, goToSlideIndex }) => (
            <React.Fragment>
              {
                (type !== 'marquee' && paginationType === 'arrows') && (
                  <div className={styles.buttonContainer}>
                    <SliderButton
                      className={styles.navButton}
                      direction="prev"
                    >
                      <Button
                        type="tertiary"
                        htmlElement="span"
                        icon="circleChevronLeft"
                        iconSize={40}
                        iconFill={colors.secondary}
                        disabled={currentSlideIndex === 0}
                      />
                    </SliderButton>
                    <SliderButton
                      className={styles.navButton}
                      direction="next"
                    >
                      <Button
                        type="tertiary"
                        htmlElement="span"
                        icon="circleChevronRight"
                        iconSize={40}
                        iconFill={colors.secondary}
                        disabled={currentSlideIndex === (sliderWithBlocks.length || 0) - slidesToShow}
                      />
                    </SliderButton>
                  </div>
                )
              }
              <SliderTrack className={constructClassName([
                styles.sliderTrack,
                type === 'carousel' && styles.carouselSliderTrack,
                type === 'marquee' && styles.marqueeSliderTrack,
                slidesToShow === 1 && styles.noLeftPaddingSliderTrack,
              ])}
              >
                {
                  sliderWithBlocks.map((block, index) => (
                    <Slide
                      key={index}
                      index={index}
                      className={constructClassName([
                        styles.slideContainer,
                        paginationType === 'blockName' && styles.blockNameSlideContainer,
                      ])}
                    >
                      <div
                        className={
                          constructClassName([
                            styles.slide,
                            slidesToShow === 1 && styles.noLeftMarginSlide,
                            (type === 'carousel' && slidesToShow === 3) && (currentSlideIndex + 1) === index && styles.middleSlide,
                          ])
                        }
                      >
                        <RenderBlocks
                          blockSettings={{
                            margin: {
                              // eslint-disable-next-line camelcase
                              marginBottom: BlockSettings_Margin_MarginBottom.None,
                            },
                            padding: {
                              // eslint-disable-next-line camelcase
                              paddingLeft: BlockSettings_Padding_PaddingLeft.None,
                              // eslint-disable-next-line camelcase
                              paddingRight: BlockSettings_Padding_PaddingRight.None,
                            },
                          }}
                          parentBlockType={`${blockType}`}
                          key={index}
                          blocks={[block]}
                        />
                      </div>
                    </Slide>
                  ))
                }
              </SliderTrack>
              {
                (type !== 'marquee' && paginationType === 'dots') && (
                  <div className={styles.dotNavigationContainer}>
                    {
                      Array.from({
                        length: Math.ceil((
                          sliderWithBlocks.length || 0
                        ) / slidesToShow),
                      }).map((_, index) => (
                        <Button
                          aria-label={`Go to slide ${index * slidesToShow + 1}`}
                          key={index}
                          type="tertiary"
                          className={constructClassName([
                            styles.dot,
                            currentSlideIndex >= index * slidesToShow && currentSlideIndex < (index + 1) * slidesToShow && styles.activeDot,
                          ])}
                          onClick={() => goToSlideIndex(index * slidesToShow)}
                        />
                      ))
                    }
                  </div>
                )
              }
              {
                (type === 'slider' && paginationType === 'blockName') && (
                  <div className={styles.blockNameContainer}>
                    {
                      sliderWithBlocks.map((block, index) => (
                        <Button
                          key={index}
                          type="tertiary"
                          className={constructClassName([
                            styles.blockNameButton,
                            currentSlideIndex === index && styles.activeBlockNameButton,
                          ])}
                          onClick={() => goToSlideIndex(index)}
                        >
                          {block.blockName}
                        </Button>
                      ))
                    }
                  </div>
                )
              }
            </React.Fragment>
          )
        }
      </SliderProvider>
    </BlockContainer>
  );
};
